import { CustomerType } from '@model/Customer';
import { initials } from '@packages/strings';
import * as Yup from 'yup';
import { Model, ModelClass } from './Model';

export type UserViewType = 'admin' | 'ops' | 'salesman' | 'designer' | 'developer' | 'customer' | 'wholesaler' | 'factory' | string;

export interface UserType extends ModelClass {
    $is(type: UserViewType | string): boolean;
    $isFactory(type: UserViewType | string): boolean;
    $isWholesaler(type: UserViewType | string): boolean;
    $fullName(): string;

    $initials(): string;

    full_name: string;
    id: number;
    thumbnail: string;
    created_at: Date;
    email: string;
    user_view: UserViewType;
    view: string;
    fanshop: string;
    first_name: string;
    last_name: string;
    name: string;
    country_id: number;
    country_name: string;
    state: string;
    city_name: string;
    phone: string;
    getFullPhone?: string;
    biography?: string;
    timezone: string;
    language: string;
    language_id: number;
    country_calling_code_id: number;
    customer_id: number;
     factory_id: number;
    factory_name: string;
    roles: any;
    salesman_id: number;
    salesman_email: string;
    category_id: number;
    category: string;
    user_at_apparel: string;
    organization_name: string;
    organization_type: string;
    sport_id: boolean;
    allow_emails: boolean;
    verified: boolean;
    customer: CustomerType;
    source: string;
    wholesaler: any;
    is_wholesaler_rep: boolean;
    user_role: UserViewType;
}

export const User = Model<UserType>('User', { endpoint: 'users' })`
    id:int;
    thumbnail:string;
    created_at(date, timezone):DateTime;
    email:string;
    verified:boolean;
    user_view->view: string;
    fanshop:string;
    first_name:string;
    last_name:string;
    name:string;
    state:string;
    country_id:int;
    country_name:string;
    city_name:string;
    phone:string;
    getFullPhone:string;
    biography:string;
    timezone:string;
    roles:Any;
    language(code,name,id):Any;
    language_id: int;
    country_calling_code_id: int;
    customer_id: int;
    factory_id: int;
        factory_name: string;
    salesman_id: int;
    category_id: int;
    category: string;
    user_at_apparel: string;
    allow_emails: boolean;
    customer: Any;
    source: string;
    user_role: string;
    wholesaler: Any;
    is_wholesaler_rep:boolean;
`;

User.$define('$is', ($self: UserType, type: string): boolean => {
    const roles = type.split('|');
    if (roles.length > 1 && roles[0] === 'wholesaler' && ($self.user_view || '').toLowerCase() === 'wholesaler') {
        return $self.$isWholesaler(roles[1]);
    }

    if (roles.length > 1 && roles[0] === 'factory' && ($self.user_view || '').toLowerCase() === 'factory') {
        return $self.$isFactory(roles[1]);
    }

    return ($self.user_view || '').toLowerCase() === type.toLowerCase();
});

User.$define('$isFactory', ($self: UserType, role: string): boolean => {
    return ($self.user_role || '').toLowerCase() === role.toLowerCase();
});

User.$define('$isWholesaler', ($self: UserType, role: string): boolean => {
    return ($self.user_role || '').toLowerCase() === role.toLowerCase();
});

User.$define('$fullName', ($self: UserType): string => {
    if ($self.full_name) {
        return `${$self.full_name}`;
    }

    return `${$self.first_name || ''} ${$self.last_name || ''}`.trim();
});

User.$define('$initials', ($self: UserType): string => initials($self.$fullName()));

export const UserValidationRules = {
    first_name: Yup.string().required('First Name is required'),
    last_name: Yup.string().required('Last Name is required'),

    email: Yup.string().required('E-Mail is required').email('E-Mail is invalid'),

    phone: Yup.number().required('phone is required').typeError('Phone must be a number'),
};

export const UserValidation = Yup.object(UserValidationRules);

export const UserRegisterValidation = Yup.object({
    first_name: Yup.string().required('Field is Required!'),
    last_name: Yup.string().required('Field is Required!'),

    organization_name: Yup.string().trim().required('Field is Required!'),

    email: Yup.string().required('Field is Required!').email('E-mail is invalid!').max(255, 'E-mail is too big!'),

    country: Yup.object({
        id: Yup.number().required('Id is required'),
        name: Yup.string().required('Name is required'),
    }),

    state: Yup.string().required('Field is Required!'),

    city_name: Yup.string().required('Field is Required!'),

    phone: Yup.object({
        phone: Yup.string()
            .trim()
            .when('country_code', {
                is: 'us',
                then: Yup.string()
                    .trim()
                    .when('phone', {
                        is: (val) => val?.length >= 1,
                        then: Yup.string().trim().min(11, 'Invalid format').required('Phone is required'),
                    }),
                otherwise: Yup.string().trim().min(4, 'Invalid format').required('Phone is required'),
            })
            .required('Phone is required'),
        country_code: Yup.string().trim(),
    }),

    sport: Yup.string().required('Field is Required!'),

    sport_other: Yup.string().when('sport', {
        is: (value) => value === 'Other',
        then: Yup.string().required('Field is Required!'),
    }),

    source: Yup.string().required('Field is Required!'),

    source_other: Yup.string().when('source', {
        is: (value) => value === 'Other',
        then: Yup.string().required('Field is Required!'),
    }),

    what_role: Yup.string().required('Field is Required!'),

    what_role_other: Yup.string().when('what_role', {
        is: (value) => value === 'Other',
        then: Yup.string().required('Field is Required!'),
    }),

    authorized_to_order: Yup.mixed().oneOf([true, false], 'Field is Required!').required('Field is Required!'),

    instagram_handle: Yup.string(),

    password: Yup.string().required('Field is Required!').min(6, 'Password should be at least 6 characters!'),
});
export const UserClaimRegisterValidation = Yup.object({
    full_name: Yup.string().required('Field is Required!'),

    email: Yup.string().required('Field is Required!').email('E-mail is invalid!').max(255, 'E-mail is too big!'),

    phone: Yup.object({
        phone: Yup.number().required('Phone is required').typeError('Phone must be a number'),
    }),

    password: Yup.string().required('Field is Required!').min(6, 'Password should be at least 6 characters!'),
});
